import { useRequireSignIn } from "@/hooks/use-require-sign-in";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Leaf,
  PenLine,
  Sparkles,
  Users,
  CalendarHeart,
  Stethoscope,
  Check,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/member-welcome")({
  head: () => ({
    meta: [
      { title: "Welcome to Recovery Trauma™ — Member Onboarding" },
      {
        name: "description",
        content:
          "A warm welcome to Recovery Trauma™. Discover how the platform is organised and where to begin your wellbeing journey.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MemberWelcomePage,
});

const SEEN_KEY_PREFIX = "rt:member-welcome-seen:";
const SEEN_KEY_ANON = "rt:member-welcome-seen";

type Pillar = {
  emoji: string;
  icon: typeof BookOpen;
  title: string;
  blurb: string;
  items: string[];
  tint: string;
};

const PILLARS: Pillar[] = [
  {
    emoji: "📚",
    icon: BookOpen,
    title: "Understand",
    blurb: "Learn more about trauma, emotions, relationships, and wellbeing.",
    items: ["Recovery Library"],
    tint: "from-amber-100/60 to-amber-50/40",
  },
  {
    emoji: "🌿",
    icon: Leaf,
    title: "Regulate",
    blurb:
      "Discover practical tools to support emotional regulation and everyday wellbeing.",
    items: [
      "Daily Check-In",
      "Grounding",
      "Breathing",
      "EFT Tapping",
      "Somatic Practices",
      "Sleep Support",
      "Calming Sounds",
    ],
    tint: "from-emerald-100/60 to-emerald-50/40",
  },
  {
    emoji: "✍️",
    icon: PenLine,
    title: "Process",
    blurb: "Reflect on your thoughts, emotions, and experiences.",
    items: [
      "Recovery Journal",
      "Dream Journal",
      "Shadow Work",
      "Creative Reflection",
      "Drawing Board",
    ],
    tint: "from-rose-100/60 to-rose-50/40",
  },
  {
    emoji: "🌱",
    icon: Sparkles,
    title: "Integrate",
    blurb: "Bring supportive wellbeing practices into everyday life.",
    items: [
      "Daily Support",
      "Daily Routines",
      "Creative Expression",
      "Meaning & Purpose",
      "Healthy Relationships",
      "Joy & Play",
      "Nature Connection",
      "Relaxation Practices",
      "Aromatherapy",
      "Forest Bathing",
      "Gardening",
    ],

    tint: "from-sky-100/60 to-sky-50/40",
  },
];

const EXTRAS: { emoji: string; icon: typeof Users; title: string; blurb: string }[] = [
  {
    emoji: "👥",
    icon: Users,
    title: "Community",
    blurb: "Connect with others in a respectful and supportive environment.",
  },
  {
    emoji: "📅",
    icon: CalendarHeart,
    title: "Live Events",
    blurb: "Take part in live wellbeing workshops and educational events.",
  },
  {
    emoji: "👩‍⚕️",
    icon: Stethoscope,
    title: "Find a Therapist",
    blurb:
      "Discover trauma-informed therapists and wellbeing professionals through our directory.",
  },
];

const STEPS = ["Welcome", "Your Journey", "More to Explore", "Begin"] as const;

export function markMemberWelcomeSeen(userId?: string | null) {
  try {
    if (userId) localStorage.setItem(SEEN_KEY_PREFIX + userId, "1");
    localStorage.setItem(SEEN_KEY_ANON, "1");
  } catch {}
}

export function hasSeenMemberWelcome(userId?: string | null) {
  try {
    if (userId && localStorage.getItem(SEEN_KEY_PREFIX + userId) === "1") return true;
    return localStorage.getItem(SEEN_KEY_ANON) === "1";
  } catch {
    return true;
  }
}

function MemberWelcomePage() {
  const signedIn = useRequireSignIn();
  if (!signedIn) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 text-sm text-muted-foreground" role="status">
        Checking you're signed in…
      </div>
    );
  }
  return <MemberWelcomePageInner />;
}

function MemberWelcomePageInner() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dontShow, setDontShow] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  // Scroll to the very top on each step change so the user never has to
  // manually scroll back up after pressing Next.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    if (dontShow) markMemberWelcomeSeen(userId);
    navigate({ to: "/personalise" });
  };


  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="relative min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--sand,#F7F3EA)]/40">
      <div className="absolute end-4 top-4 z-10">
        <button
          onClick={finish}
          className="rounded-full border border-border bg-background/70 px-3 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur transition hover:text-foreground"
        >
          Skip for now
        </button>
      </div>

      <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        {/* progress */}
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.22em] text-[var(--gold-deep)]">
            <span>
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </span>
            <span className="text-muted-foreground/80">Take your time</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gold/15">
            <div
              className="h-full rounded-full bg-[var(--gradient-gold)] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* card */}
        <div className="rounded-[2rem] border border-gold/25 bg-card/95 p-7 shadow-[var(--shadow-soft)] backdrop-blur sm:p-10">
          {step === 0 && (
            <div className="text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-3xl shadow-[var(--shadow-gold)]">
                💛
              </div>
              <h1 className="mt-6 font-display text-3xl text-foreground sm:text-4xl">
                Welcome to Recovery Trauma™
              </h1>
              <p className="mt-3 text-base text-muted-foreground">
                We're so glad you're here.
              </p>
              <div className="mx-auto mt-6 max-w-xl space-y-4 text-[15px] leading-relaxed text-foreground/85">
                <p>
                  Recovery Trauma™ is a survivor-created, trauma-informed
                  wellbeing platform designed to support your wellbeing through
                  education, practical tools, self-reflection, and community.
                </p>
                <p>
                  There is no right or wrong place to begin. Explore at your own
                  pace and choose the tools that feel most helpful for you.
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="text-center">
                <p className="text-xs font-medium uppercase tracking-[0.22em] text-[var(--gold-deep)]">
                  🌱 Your Wellbeing Journey
                </p>
                <h2 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
                  Four simple areas to help you get started
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  We've organised Recovery Trauma™ into four gentle pillars.
                  Begin wherever feels right for you today.
                </p>
              </div>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                {PILLARS.map((p) => (
                  <li
                    key={p.title}
                    className={`relative overflow-hidden rounded-2xl border border-gold/25 bg-gradient-to-br ${p.tint} p-5`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-background/80 text-xl shadow-sm">
                        {p.emoji}
                      </div>
                      <h3 className="font-display text-lg text-foreground">
                        {p.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                      {p.blurb}
                    </p>
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {p.items.map((it) => (
                        <li
                          key={it}
                          className="rounded-full border border-gold/30 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-foreground/75"
                        >
                          {it}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="text-center">
                <p className="text-xs font-medium uppercase tracking-[0.22em] text-[var(--gold-deep)]">
                  Beyond the tools
                </p>
                <h2 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
                  More ways to feel supported
                </h2>
              </div>
              <ul className="mt-8 space-y-4">
                {EXTRAS.map((e) => {
                  const Icon = e.icon;
                  return (
                    <li
                      key={e.title}
                      className="flex items-start gap-4 rounded-2xl border border-gold/25 bg-card p-5 shadow-sm"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-xl shadow-[var(--shadow-gold)]">
                        {e.emoji}
                        <Icon className="sr-only" aria-hidden />
                      </div>
                      <div>
                        <h3 className="font-display text-lg text-foreground">
                          {e.title}
                        </h3>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {e.blurb}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {step === 3 && (
            <div className="text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-3xl shadow-[var(--shadow-gold)]">
                🌟
              </div>
              <h2 className="mt-6 font-display text-3xl text-foreground">
                A gentle invitation to begin
              </h2>
              <div className="mx-auto mt-5 max-w-xl space-y-4 text-[15px] leading-relaxed text-foreground/85">
                <p>
                  Recovery is personal, and everyone's journey is different.
                </p>
                <p>
                  We encourage you to take your time, explore the platform, and
                  return to the tools that feel most supportive for you.
                </p>
                <p>
                  Thank you for becoming part of the Recovery Trauma™ community.
                  We're honoured to be part of your wellbeing journey.
                </p>
                <p className="font-display text-base text-[var(--gold-deep)]">
                  💛 Jana
                </p>
              </div>

              <label className="mx-auto mt-8 flex max-w-sm items-center justify-center gap-2 rounded-full border border-gold/30 bg-card/80 px-4 py-2.5 text-sm text-foreground/80">
                <input
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  className="h-4 w-4 rounded border-gold/40 text-[var(--gold-deep)] focus:ring-[var(--gold-deep)]"
                />
                Don't show this again
              </label>
            </div>
          )}
        </div>

        {/* nav */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition disabled:opacity-30 enabled:hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          <div className="flex gap-1.5" aria-hidden>
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-[var(--gold-deep)]" : "w-1.5 bg-gold/30"
                }`}
              />
            ))}
          </div>

          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-5 py-2.5 font-display text-sm font-medium text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={finish}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-5 py-2.5 font-display text-sm font-medium text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
            >
              <Check className="h-4 w-4" /> Start Exploring
            </button>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/help"
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            You can revisit this welcome anytime from Help &amp; FAQ → Getting Started
          </Link>
        </div>
      </div>
    </div>
  );
}
